import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useApiClient } from '../api/ApiClientProvider';
import type { ApiError } from '../api/ApiError';
import { raceKeys } from './queryKeys';
import type { Contestant, Organizer, RaceEventDetail, RecordResultBody, RegisterContestantBody, SaveRaceEventBody } from './types';
import { unwrap, unwrapEmpty } from './unwrap';

/** Invalidates every racing query, used after any change with wide effects. */
function useInvalidateRacing(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: raceKeys.all });
  };
}

/** Creates the calling user's organiser profile. */
export function useCreateOrganizer(): UseMutationResult<Organizer, ApiError, { name: string }> {
  const client = useApiClient();
  const invalidate = useInvalidateRacing();

  return useMutation<Organizer, ApiError, { name: string }>({
    mutationFn: async (body) => unwrap(await client.POST('/api/racing/organizers', { body }), 'Could not create your organiser profile'),
    onSuccess: async () => invalidate(),
  });
}

/** Creates a race event in draft. */
export function useCreateRaceEvent(): UseMutationResult<RaceEventDetail, ApiError, SaveRaceEventBody> {
  const client = useApiClient();
  const invalidate = useInvalidateRacing();

  return useMutation<RaceEventDetail, ApiError, SaveRaceEventBody>({
    mutationFn: async (body) => unwrap(await client.POST('/api/racing/events', { body }), 'Could not create the race event'),
    onSuccess: async () => invalidate(),
  });
}

/** Updates a race event's fields. */
export function useUpdateRaceEvent(raceEventId: string): UseMutationResult<RaceEventDetail, ApiError, SaveRaceEventBody> {
  const client = useApiClient();
  const invalidate = useInvalidateRacing();

  return useMutation<RaceEventDetail, ApiError, SaveRaceEventBody>({
    mutationFn: async (body) =>
      unwrap(
        await client.PUT('/api/racing/events/{raceEventId}', { params: { path: { raceEventId } }, body }),
        'Could not update the race event',
      ),
    onSuccess: async () => invalidate(),
  });
}

/** The variables for a lifecycle transition: only the concurrency token. */
type TransitionVariables = { version?: string | null };

function useTransition(
  raceEventId: string,
  path: '/api/racing/events/{raceEventId}/open' | '/api/racing/events/{raceEventId}/bracket' | '/api/racing/events/{raceEventId}/complete' | '/api/racing/events/{raceEventId}/cancel',
  fallback: string,
): UseMutationResult<RaceEventDetail, ApiError, TransitionVariables> {
  const client = useApiClient();
  const invalidate = useInvalidateRacing();

  return useMutation<RaceEventDetail, ApiError, TransitionVariables>({
    mutationFn: async ({ version }) =>
      unwrap(await client.POST(path, { params: { path: { raceEventId } }, body: { version: version ?? null } }), fallback),
    onSuccess: async () => invalidate(),
  });
}

/** Opens a draft event for registration. */
export function useOpenRegistration(raceEventId: string): UseMutationResult<RaceEventDetail, ApiError, TransitionVariables> {
  return useTransition(raceEventId, '/api/racing/events/{raceEventId}/open', 'Could not open registration');
}

/** Locks the field and generates the bracket. */
export function useGenerateBracket(raceEventId: string): UseMutationResult<RaceEventDetail, ApiError, TransitionVariables> {
  return useTransition(raceEventId, '/api/racing/events/{raceEventId}/bracket', 'Could not generate the bracket');
}

/** Marks a running event complete. */
export function useCompleteRaceEvent(raceEventId: string): UseMutationResult<RaceEventDetail, ApiError, TransitionVariables> {
  return useTransition(raceEventId, '/api/racing/events/{raceEventId}/complete', 'Could not complete the event');
}

/** Cancels an event. */
export function useCancelRaceEvent(raceEventId: string): UseMutationResult<RaceEventDetail, ApiError, TransitionVariables> {
  return useTransition(raceEventId, '/api/racing/events/{raceEventId}/cancel', 'Could not cancel the event');
}

/** Registers a contestant. */
export function useRegisterContestant(raceEventId: string): UseMutationResult<Contestant, ApiError, RegisterContestantBody> {
  const client = useApiClient();
  const invalidate = useInvalidateRacing();

  return useMutation<Contestant, ApiError, RegisterContestantBody>({
    mutationFn: async (body) =>
      unwrap(
        await client.POST('/api/racing/events/{raceEventId}/contestants', { params: { path: { raceEventId } }, body }),
        'Could not register the contestant',
      ),
    onSuccess: async () => invalidate(),
  });
}

/** Withdraws a contestant. */
export function useWithdrawContestant(
  raceEventId: string,
): UseMutationResult<void, ApiError, { contestantId: string; version?: string | null }> {
  const client = useApiClient();
  const invalidate = useInvalidateRacing();

  return useMutation<void, ApiError, { contestantId: string; version?: string | null }>({
    mutationFn: async ({ contestantId, version }) =>
      unwrapEmpty(
        await client.DELETE('/api/racing/events/{raceEventId}/contestants/{contestantId}', {
          params: { path: { raceEventId, contestantId }, query: version == null ? {} : { version } },
        }),
        'Could not withdraw the contestant',
      ),
    onSuccess: async () => invalidate(),
  });
}

/** Records a timing result and returns the updated event. */
export function useRecordResult(raceEventId: string): UseMutationResult<RaceEventDetail, ApiError, RecordResultBody> {
  const client = useApiClient();
  const invalidate = useInvalidateRacing();

  return useMutation<RaceEventDetail, ApiError, RecordResultBody>({
    mutationFn: async (body) =>
      unwrap(
        await client.POST('/api/racing/events/{raceEventId}/results', { params: { path: { raceEventId } }, body }),
        'Could not record the result',
      ),
    onSuccess: async () => invalidate(),
  });
}
